import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { QuotationStatus } from '@kalachand/shared';
import { apiGet } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { PageScaffold } from '../../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../../components/DataTable';
import { Button } from '../../../components/Button';
import { Money } from '../../../components/Money';
import { StatusPill } from '../../../components/StatusPill';
import { EmptyState } from '../../../components/EmptyState';
import { Pagination } from '../../../components/Pagination';
import { cn } from '../../../lib/cn';
import { rowNav } from '../../../lib/rowNav';
import { DocumentExportButtons } from '../../../components/DocumentExportButtons';

interface QuotationListRow {
  id: string;
  number: string;
  status: QuotationStatus;
  total: string;
  validUntil: string;
  createdAt: string;
  shop: { id: string; code: string; name: string };
  customer: {
    id: string;
    name: string;
    companyName: string | null;
    type: 'individual' | 'company';
    phone: string;
  };
  salesperson: { id: string; name: string };
  followUps: {
    outcome: string;
    note: string | null;
    createdAt: string;
    user: { name: string };
  }[];
  _count: { followUps: number };
}

type Tab = 'all' | 'pending' | 'followed_up' | 'won' | 'lost' | 'expired';

const TABS: { key: Tab; label: string; matches: (s: QuotationStatus) => boolean }[] = [
  { key: 'all', label: 'All', matches: () => true },
  { key: 'pending', label: 'New', matches: (s) => s === 'PENDING' },
  { key: 'followed_up', label: 'Followed up', matches: (s) => s === 'FOLLOWED_UP' },
  { key: 'won', label: 'Won', matches: (s) => s === 'WON' },
  { key: 'lost', label: 'Lost', matches: (s) => s === 'LOST' },
  { key: 'expired', label: 'Expired', matches: (s) => s === 'EXPIRED' },
];

const PAGE_SIZE = 20;

function statusTone(
  s: QuotationStatus,
): 'positive' | 'warning' | 'negative' | 'neutral' | 'accent' {
  switch (s) {
    case 'WON':
      return 'positive';
    case 'LOST':
      return 'negative';
    case 'EXPIRED':
      return 'neutral';
    case 'FOLLOWED_UP':
      return 'accent';
    default:
      return 'warning';
  }
}

function daysBetween(target: Date, now: Date = new Date()): number {
  const diff = target.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function outcomeLabel(o: string): string {
  return o
    .toLowerCase()
    .split('_')
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(' ');
}

function tabKeyFor(s: QuotationStatus): Tab {
  return s === 'PENDING'
    ? 'pending'
    : s === 'FOLLOWED_UP'
      ? 'followed_up'
      : s === 'WON'
        ? 'won'
        : s === 'LOST'
          ? 'lost'
          : 'expired';
}

function displayName(c: QuotationListRow['customer']): string {
  return c.type === 'company' ? (c.companyName ?? c.name) : c.name;
}

export function QuotationsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('all');
  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);

  const quotations = useQuery({
    queryKey: ['quotations'],
    queryFn: () => apiGet<QuotationListRow[]>('/quotations'),
    refetchInterval: 60_000,
  });

  // Search + date applied first (independent of the status tab) so the tab
  // count badges reflect what's actually matchable.
  const searchDateFiltered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const now = Date.now();
    const cutoff = (() => {
      switch (dateRange) {
        case 'today': {
          const d = new Date();
          d.setHours(0, 0, 0, 0);
          return d.getTime();
        }
        case '7d':
          return now - 7 * 86_400_000;
        case '30d':
          return now - 30 * 86_400_000;
        case 'month': {
          const d = new Date();
          return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
        }
        default:
          return 0;
      }
    })();
    return (quotations.data ?? []).filter((row) => {
      if (
        ql &&
        !`${row.number} ${displayName(row.customer)} ${row.customer.name} ${row.customer.phone}`
          .toLowerCase()
          .includes(ql)
      )
        return false;
      if (cutoff && new Date(row.createdAt).getTime() < cutoff) return false;
      return true;
    });
  }, [quotations.data, q, dateRange]);

  const counts = useMemo(() => {
    const out: Record<Tab, number> = {
      all: 0,
      pending: 0,
      followed_up: 0,
      won: 0,
      lost: 0,
      expired: 0,
    };
    for (const row of searchDateFiltered) {
      out.all += 1;
      out[tabKeyFor(row.status)] += 1;
    }
    return out;
  }, [searchDateFiltered]);

  const filtered = useMemo(() => {
    const tabDef = TABS.find((t) => t.key === tab)!;
    const list = searchDateFiltered.filter((row) => tabDef.matches(row.status));
    const total = (x: QuotationListRow): number => Number(x.total);
    const at = (x: QuotationListRow): number => new Date(x.createdAt).getTime();
    const valid = (x: QuotationListRow): number => new Date(x.validUntil).getTime();
    list.sort((a, z) =>
      sort === 'oldest'
        ? at(a) - at(z)
        : sort === 'total_desc'
          ? total(z) - total(a)
          : sort === 'total_asc'
            ? total(a) - total(z)
            : sort === 'expiring'
              ? valid(a) - valid(z)
              : at(z) - at(a),
    );
    return list;
  }, [searchDateFiltered, tab, sort]);

  const hasFilters = !!(q || dateRange !== 'all' || sort !== 'newest' || tab !== 'all');
  const clearFilters = (): void => {
    setQ('');
    setDateRange('all');
    setSort('newest');
    setTab('all');
  };

  // Pagination over the filtered set. Reset to page 1 whenever filters change.
  useEffect(() => {
    setPage(1);
  }, [q, dateRange, sort, tab]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <PageScaffold
      scroll={false}
      title="Quotations"
      subtitle="Price proposals you've sent to customers. Valid 10 days · follow up before they expire."
      actions={
        <>
          <DocumentExportButtons document="quotations" />
          <Link to="/quotations/new">
            <Button>+ New quotation</Button>
          </Link>
        </>
      }
    >
      <Card className="shrink-0" bodyClassName="p-4 space-y-3">
        {/* Status tabs */}
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs uppercase tracking-wide transition border',
                tab === t.key
                  ? 'bg-accent text-white border-accent'
                  : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40',
              )}
            >
              <span>{t.label}</span>
              <span
                className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded',
                  tab === t.key ? 'bg-white/20 text-white' : 'bg-surface text-ink-3',
                )}
              >
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Search · date · sort */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end pt-2 border-t border-rule-2">
          <div className="md:col-span-6">
            <FilterField label="Search">
              <input
                className="kc-input"
                placeholder="Quote no, customer, or phone…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </FilterField>
          </div>
          <FilterSelect
            label="Date"
            value={dateRange}
            onChange={setDateRange}
            className="md:col-span-3"
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="month">This month</option>
          </FilterSelect>
          <FilterSelect label="Sort" value={sort} onChange={setSort} className="md:col-span-3">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="total_desc">Total: high → low</option>
            <option value="total_asc">Total: low → high</option>
            <option value="expiring">Expiring soonest</option>
          </FilterSelect>
        </div>
        <div className="flex items-center justify-between text-xs text-ink-3">
          <span>
            {filtered.length} quotation{filtered.length === 1 ? '' : 's'}
          </span>
          {hasFilters && (
            <Button variant="tertiary" size="xs" onClick={clearFilters}>
              Clear all filters
            </Button>
          )}
        </div>
      </Card>

      <DataTableCard
        footer={
          filtered.length > 0 ? (
            <Pagination
              page={safePage}
              pageCount={pageCount}
              total={filtered.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              unit="quotation"
            />
          ) : undefined
        }
      >
        {quotations.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No quotations here"
            description={
              hasFilters
                ? 'Try a different filter or search.'
                : "You haven't sent any quotations yet."
            }
            action={
              hasFilters ? (
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : (
                <Link to="/quotations/new">
                  <Button>+ New quotation</Button>
                </Link>
              )
            }
          />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">Quote</th>
                <th className="px-6 py-2 font-medium">Customer</th>
                <th className="px-6 py-2 font-medium">Shop</th>
                <th className="px-6 py-2 font-medium text-right">Total</th>
                <th className="px-6 py-2 font-medium">Status</th>
                <th className="px-6 py-2 font-medium">Validity</th>
                <th className="px-6 py-2 font-medium">Last follow-up</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {paged.map((row) => {
                const display = displayName(row.customer);
                const validUntil = new Date(row.validUntil);
                const days = daysBetween(validUntil);
                const last = row.followUps[0];
                const isLive = row.status === 'PENDING' || row.status === 'FOLLOWED_UP';
                const expiringSoon = isLive && days >= 0 && days <= 5;
                const expiredCutoff = isLive && days < 0;
                return (
                  <tr
                    key={row.id}
                    onClick={rowNav(navigate, `/quotations/${row.id}`)}
                    className="border-b border-rule-2 hover:bg-paper-2 cursor-pointer"
                  >
                    <td className="px-6 py-3 align-top">
                      <Link
                        to={`/quotations/${row.id}`}
                        className="kc-mono text-xs text-ink hover:text-accent"
                      >
                        {row.number}
                      </Link>
                      <div className="text-[10px] text-ink-3 mt-0.5">
                        {new Date(row.createdAt).toLocaleDateString('en-MU', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-3 align-top">
                      <div className="text-ink">{display}</div>
                      <div className="text-xs text-ink-3 kc-mono">{row.customer.phone}</div>
                    </td>
                    <td className="px-6 py-3 align-top">
                      <StatusPill>{row.shop.code}</StatusPill>
                    </td>
                    <td className="px-6 py-3 text-right align-top kc-mono">
                      <Money value={row.total} className="text-ink" />
                    </td>
                    <td className="px-6 py-3 align-top">
                      <StatusPill tone={statusTone(row.status)}>
                        {row.status.replace('_', ' ').toLowerCase()}
                      </StatusPill>
                    </td>
                    <td className="px-6 py-3 align-top">
                      <div className="text-xs text-ink-2">
                        {validUntil.toLocaleDateString('en-MU', { day: 'numeric', month: 'short' })}
                      </div>
                      {isLive && (
                        <div
                          className={cn(
                            'text-[10px] mt-0.5',
                            expiredCutoff
                              ? 'text-negative'
                              : expiringSoon
                                ? 'text-warning'
                                : 'text-ink-3',
                          )}
                        >
                          {expiredCutoff ? `Expired ${-days}d ago` : `${days}d left`}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3 align-top">
                      {last ? (
                        <div>
                          <div className="text-xs text-ink-2">{outcomeLabel(last.outcome)}</div>
                          <div className="text-[10px] text-ink-3 mt-0.5">
                            {new Date(last.createdAt).toLocaleDateString('en-MU', {
                              day: 'numeric',
                              month: 'short',
                            })}{' '}
                            · {last.user.name}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-3 italic">Not followed up</span>
                      )}
                    </td>
                    <td className="px-6 py-3 align-top text-right">
                      <Link to={`/quotations/${row.id}`}>
                        <Button size="sm" variant="ghost">
                          Open
                        </Button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  className,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className={cn('flex flex-col gap-1', className)}>
      <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="kc-input">
        {children}
      </select>
    </label>
  );
}
